/**
 * ResultsGrid — siatka wyników zapytania (react-data-grid, wirtualizowana).
 *
 * Nagłówek kolumny: nazwa + typ pod spodem (jak w pgAdmin), plakietka „maska" przy
 * kolumnach wrażliwych. Sortowanie po kliknięciu w nagłówek działa lokalnie (na pobranych
 * wierszach). NULL wyróżniony szarą kursywą. Ctrl+C kopiuje aktywną komórkę.
 */

import { useMemo, useState } from 'react';
import { type Column, DataGrid, type SortColumn } from 'react-data-grid';
import { useTranslation } from 'react-i18next';

import { useIsDarkTheme } from '@/contexts/ThemeContext';
import type { DbResultColumn } from '@/lib/db-browser/types';
import { cn } from '@/lib/utils';

import 'react-data-grid/lib/styles.css';

interface GridRow {
  n: number;
  cells: (string | null)[];
}

interface ResultsGridProps {
  columns: DbResultColumn[];
  rows: (string | null)[][];
  className?: string;
  /** Rysuj tylko widoczne wiersze/kolumny (domyślnie). Wyłączane w testach (jsdom nie ma wymiarów). */
  virtualize?: boolean;
}

function compareCells(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const na = Number(a);
  const nb = Number(b);
  if (a.trim() !== '' && b.trim() !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return a.localeCompare(b, 'pl');
}

export function ResultsGrid({ columns, rows, className, virtualize = true }: ResultsGridProps) {
  const { t } = useTranslation();
  const isDark = useIsDarkTheme();
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  const gridRows = useMemo<GridRow[]>(() => rows.map((cells, i) => ({ n: i + 1, cells })), [rows]);

  const sortedRows = useMemo(() => {
    const sort = sortColumns[0];
    if (!sort || sort.columnKey === '__n') return gridRows;
    const idx = Number(sort.columnKey.slice(1));
    const dir = sort.direction === 'ASC' ? 1 : -1;
    return [...gridRows].sort(
      (a, b) => dir * compareCells(a.cells[idx] ?? null, b.cells[idx] ?? null),
    );
  }, [gridRows, sortColumns]);

  const gridColumns = useMemo<Column<GridRow>[]>(
    () => [
      {
        key: '__n',
        name: '',
        width: 48,
        frozen: true,
        resizable: false,
        renderCell: ({ row }) => <span className="text-xs text-text-tertiary">{row.n}</span>,
        cellClass: 'text-center bg-bg-secondary',
        headerCellClass: 'bg-bg-secondary',
      },
      ...columns.map<Column<GridRow>>((c, i) => ({
        key: `c${i}`,
        name: c.name,
        resizable: true,
        sortable: true,
        minWidth: 90,
        width: 'max-content',
        renderHeaderCell: () => (
          <div
            className="flex flex-col justify-center leading-tight"
            title={c.masked ? t('admin.dbBrowser.result.maskedHint') : undefined}
          >
            <span className="flex items-center gap-1.5 font-semibold">
              {c.name}
              {c.masked && (
                <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {t('admin.dbBrowser.result.maskBadge')}
                </span>
              )}
            </span>
            <span className="font-mono text-[11px] font-normal text-text-tertiary">{c.type}</span>
          </div>
        ),
        renderCell: ({ row }) => {
          const v = row.cells[i] ?? null;
          if (v === null) return <span className="italic text-text-tertiary">[null]</span>;
          return <span className={cn(c.masked && 'tracking-widest text-text-tertiary')}>{v}</span>;
        },
      })),
    ],
    [columns, t],
  );

  return (
    <DataGrid
      aria-label={t('admin.dbBrowser.result.gridLabel')}
      columns={gridColumns}
      rows={sortedRows}
      rowKeyGetter={(r) => r.n}
      sortColumns={sortColumns}
      onSortColumnsChange={(s) => setSortColumns(s.slice(-1))}
      enableVirtualization={virtualize}
      rowHeight={28}
      headerRowHeight={44}
      onCellCopy={({ row, column }, event) => {
        if (column.key === '__n') return;
        const v = row.cells[Number(column.key.slice(1))];
        event.clipboardData.setData('text/plain', v ?? '');
        event.preventDefault();
      }}
      className={cn(isDark ? 'rdg-dark' : 'rdg-light', 'h-full text-[13px]', className)}
    />
  );
}
